// ── Early Adopter Badge System ────────────────────────────────────
// Awards badges based on when a user joined BCHNostr.
// Badges are determined by their first interaction (kind 1 with #bch tag)
// or by operator-issued badge events.

import { isOperatorPubkey } from './platform';

// Badge tiers — cutoff dates (Unix timestamps)
// Adjust these as your community grows
const BADGE_TIERS = [
  {
    id: 'early-adopter',
    name: 'Early Adopter',
    emoji: '\u{1F48E}', // \uD83D\uDC8E
    description: 'First 100 days of BCHNostr',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10 border-purple-500/30',
    cutoffDate: new Date('2026-08-01').getTime() / 1000, // ~100 days from now
  },
  {
    id: 'pioneer',
    name: 'Pioneer',
    emoji: '\u{1F680}', // 🚀
    description: 'First year of BCHNostr',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10 border-amber-500/30',
    cutoffDate: new Date('2027-04-24').getTime() / 1000, // 1 year
  },
  {
    id: 'early',
    name: 'Early Adopter',
    emoji: '\u{2B50}', // ⭐
    description: 'Joined in the first 2 years',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10 border-emerald-500/30',
    cutoffDate: new Date('2028-04-24').getTime() / 1000, // 2 years
  },
];

// Special manually-awarded badges
const SPECIAL_BADGES: Record<string, { id: string; name: string; emoji: string; description: string; color: string; bgColor: string }> = {
  'og': {
    id: 'og',
    name: 'OG',
    emoji: '\u{1F451}', // 👑
    description: 'Original BCHNostr team',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10 border-yellow-500/30',
  },
  'contributor': {
    id: 'contributor',
    name: 'Contributor',
    emoji: '\u{1F6E0}\uFE0F', // \uD83D\uDEE0\uFE0F
    description: 'Contributed to BCHNostr development',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10 border-blue-500/30',
  },
  'whale': {
    id: 'whale',
    name: 'BCH Whale',
    emoji: '\u{1F40B}', // \uD83D\uDC0B
    description: 'Major BCH supporter',
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10 border-cyan-500/30',
  },
};

// ── Ambassador Badges (earned by inviting active users) ────────────
export const RECRUITER_TIERS = [
  {
    id: 'ambassador-grand',
    name: 'Grand Ambassador',
    emoji: '\u{1F451}', // 👑
    description: 'Invited 25+ active BCHNostr users',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10 border-yellow-500/30',
    minReferrals: 25,
  },
  {
    id: 'ambassador-gold',
    name: 'BCH Nostr Ambassador',
    emoji: '\u{1F31F}', // \uD83C\uDF1F
    description: 'Invited 10+ active BCHNostr users',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10 border-amber-500/30',
    minReferrals: 10,
  },
  {
    id: 'ambassador-silver',
    name: 'BCH Ambassador',
    emoji: '\u{1F4E3}', // \uD83D\uDCE3
    description: 'Invited 5+ active BCHNostr users',
    color: 'text-slate-400',
    bgColor: 'bg-slate-400/10 border-slate-400/30',
    minReferrals: 5,
  },
  {
    id: 'advocate',
    name: 'BCH Advocate',
    emoji: '\u{1F91D}', // \uD83E\uDD1D
    description: 'Invited at least 1 active BCHNostr user',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10 border-emerald-500/30',
    minReferrals: 1,
  },
] as const;

/** Get the recruiter badge for a given confirmed referral count */
export function getRecruiterBadge(confirmedReferrals: number): Badge | null {
  for (const tier of RECRUITER_TIERS) {
    if (confirmedReferrals >= tier.minReferrals) {
      return tier;
    }
  }
  return null;
}

// Operator always gets OG badge
const OPERATOR_BADGES = ['og'];

export interface Badge {
  id: string;
  name: string;
  emoji: string;
  description: string;
  color: string;
  bgColor: string;
}

/**
 * Get badges for a user based on their account creation time.
 */
export function getBadgesForTimestamp(createdAt: number): Badge[] {
  const badges: Badge[] = [];
  for (const tier of BADGE_TIERS) {
    if (createdAt <= tier.cutoffDate) {
      badges.push(tier);
      break; // Only award the highest tier
    }
  }
  return badges;
}

/**
 * Get special badges for a pubkey (operator, manually awarded, etc.)
 */
export function getSpecialBadges(pubkey: string, awardedBadgeIds: string[] = []): Badge[] {
  const badges: Badge[] = [];

  // Operator always gets OG
  if (isOperatorPubkey(pubkey)) {
    OPERATOR_BADGES.forEach(id => {
      if (SPECIAL_BADGES[id]) badges.push(SPECIAL_BADGES[id]);
    });
  }

  // Manually awarded badges
  awardedBadgeIds.forEach(id => {
    if (SPECIAL_BADGES[id] && !badges.find(b => b.id === id)) {
      badges.push(SPECIAL_BADGES[id]);
    }
  });

  return badges;
}

/**
 * Get all badges for a user.
 */
export function getAllBadges(pubkey: string, firstSeenTimestamp: number, awardedBadgeIds: string[] = []): Badge[] {
  return [
    ...getSpecialBadges(pubkey, awardedBadgeIds),
    ...getBadgesForTimestamp(firstSeenTimestamp),
  ];
}

// ── Activity-Based Recognition Badges ─────────────────────────────
//
// Unlike the *private* trust score (which is never shown to users), these
// are PUBLIC recognition badges earned purely through measurable on-platform
// activity: posting, replying, tipping, being tipped, crowdfunding, and
// showing up consistently. They reuse the exact same signals the trust score
// already gathers (see TrustSignals in trustScore.ts), so surfacing them adds
// no extra relay queries.
//
// Each category has tiered thresholds; the user is awarded the HIGHEST tier
// they qualify for in that category. This keeps the badge row compact while
// still giving people something to climb toward.

export interface ActivityBadge extends Badge {
  /** Which activity category this badge belongs to. */
  category: ActivityCategory;
  /** The tier within the category (higher = rarer). */
  tier: number;
  /** How much more activity is needed for the *next* tier, if any. */
  nextThreshold?: number;
}

export type ActivityCategory =
  | 'poster'
  | 'conversationalist'
  | 'tipper'
  | 'beloved'
  | 'backer'
  | 'regular';

interface ActivityTier {
  id: string;
  name: string;
  emoji: string;
  description: string;
  color: string;
  bgColor: string;
  threshold: number;
}

/**
 * The activity signals this system reads. This is intentionally a subset of
 * `TrustSignals` so a caller can pass the trust score's `signals` object
 * directly without any adaptation.
 */
export interface ActivitySignals {
  postCount: number;
  meaningfulReplyCount: number;
  tipsSentCount: number;
  tipsReceivedCount: number;
  crowdfundContributions: number;
  activeDays: number;
}

// Tiers are listed HIGHEST-first so the resolver can award the top match
// and stop. Thresholds are deliberately reachable — recognition should feel
// earnable, not lottery-rare.
const ACTIVITY_TIERS: Record<ActivityCategory, ActivityTier[]> = {
  poster: [
    { id: 'poster-3', name: 'Prolific Poster', emoji: '\u{1F4E3}', description: 'Published 250+ posts', color: 'text-fuchsia-500', bgColor: 'bg-fuchsia-500/10 border-fuchsia-500/30', threshold: 250 },
    { id: 'poster-2', name: 'Active Poster', emoji: '\u{1F4DD}', description: 'Published 50+ posts', color: 'text-indigo-500', bgColor: 'bg-indigo-500/10 border-indigo-500/30', threshold: 50 },
    { id: 'poster-1', name: 'Poster', emoji: '\u{270D}\uFE0F', description: 'Published 10+ posts', color: 'text-sky-500', bgColor: 'bg-sky-500/10 border-sky-500/30', threshold: 10 },
  ],
  conversationalist: [
    { id: 'reply-3', name: 'Community Voice', emoji: '\u{1F5E3}\uFE0F', description: 'Replied 200+ times', color: 'text-rose-500', bgColor: 'bg-rose-500/10 border-rose-500/30', threshold: 200 },
    { id: 'reply-2', name: 'Conversationalist', emoji: '\u{1F4AC}', description: 'Replied 50+ times', color: 'text-teal-500', bgColor: 'bg-teal-500/10 border-teal-500/30', threshold: 50 },
    { id: 'reply-1', name: 'Engager', emoji: '\u{1F44B}', description: 'Replied 10+ times', color: 'text-cyan-500', bgColor: 'bg-cyan-500/10 border-cyan-500/30', threshold: 10 },
  ],
  tipper: [
    { id: 'tipper-3', name: 'BCH Philanthropist', emoji: '\u{1F4B8}', description: 'Sent 100+ tips', color: 'text-emerald-500', bgColor: 'bg-emerald-500/10 border-emerald-500/30', threshold: 100 },
    { id: 'tipper-2', name: 'Generous Tipper', emoji: '\u{1F381}', description: 'Sent 25+ tips', color: 'text-green-500', bgColor: 'bg-green-500/10 border-green-500/30', threshold: 25 },
    { id: 'tipper-1', name: 'Tipper', emoji: '\u{1FA99}', description: 'Sent 5+ tips', color: 'text-lime-600', bgColor: 'bg-lime-500/10 border-lime-500/30', threshold: 5 },
  ],
  beloved: [
    { id: 'beloved-3', name: 'Community Favorite', emoji: '\u{1F31F}', description: 'Received 100+ tips', color: 'text-amber-500', bgColor: 'bg-amber-500/10 border-amber-500/30', threshold: 100 },
    { id: 'beloved-2', name: 'Well Supported', emoji: '\u{1F49A}', description: 'Received 25+ tips', color: 'text-orange-500', bgColor: 'bg-orange-500/10 border-orange-500/30', threshold: 25 },
    { id: 'beloved-1', name: 'Appreciated', emoji: '\u{1F44D}', description: 'Received 5+ tips', color: 'text-yellow-600', bgColor: 'bg-yellow-500/10 border-yellow-500/30', threshold: 5 },
  ],
  backer: [
    { id: 'backer-3', name: 'Super Backer', emoji: '\u{1F3AF}', description: 'Backed 15+ crowdfunds', color: 'text-red-500', bgColor: 'bg-red-500/10 border-red-500/30', threshold: 15 },
    { id: 'backer-2', name: 'Backer', emoji: '\u{1F91D}', description: 'Backed 5+ crowdfunds', color: 'text-pink-500', bgColor: 'bg-pink-500/10 border-pink-500/30', threshold: 5 },
    { id: 'backer-1', name: 'Supporter', emoji: '\u{2764}\uFE0F', description: 'Backed a crowdfund', color: 'text-rose-500', bgColor: 'bg-rose-500/10 border-rose-500/30', threshold: 1 },
  ],
  regular: [
    { id: 'regular-3', name: 'Pillar', emoji: '\u{1F3DB}\uFE0F', description: 'Active on 180+ days', color: 'text-amber-600', bgColor: 'bg-amber-500/10 border-amber-500/30', threshold: 180 },
    { id: 'regular-2', name: 'Regular', emoji: '\u{1F525}', description: 'Active on 60+ days', color: 'text-orange-500', bgColor: 'bg-orange-500/10 border-orange-500/30', threshold: 60 },
    { id: 'regular-1', name: 'Familiar Face', emoji: '\u{1F60A}', description: 'Active on 14+ days', color: 'text-sky-500', bgColor: 'bg-sky-500/10 border-sky-500/30', threshold: 14 },
  ],
};

/** Human labels for each activity category, used by UI copy. */
export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategory, string> = {
  poster: 'Posting',
  conversationalist: 'Replies',
  tipper: 'Tips sent',
  beloved: 'Tips received',
  backer: 'Crowdfunding',
  regular: 'Consistency',
};

const ACTIVITY_CATEGORY_ORDER: ActivityCategory[] = [
  'regular', 'poster', 'conversationalist', 'tipper', 'beloved', 'backer',
];

function signalForCategory(category: ActivityCategory, s: ActivitySignals): number {
  switch (category) {
    case 'poster': return s.postCount;
    case 'conversationalist': return s.meaningfulReplyCount;
    case 'tipper': return s.tipsSentCount;
    case 'beloved': return s.tipsReceivedCount;
    case 'backer': return s.crowdfundContributions;
    case 'regular': return s.activeDays;
  }
}

/**
 * Resolve the earned activity badges for a set of activity signals.
 *
 * For each category the user is awarded the single HIGHEST tier they meet.
 * The result is ordered so the "consistency" badge leads, then content, then
 * generosity — a stable order that reads well in a compact row.
 */
export function getActivityBadges(signals: ActivitySignals | undefined | null): ActivityBadge[] {
  if (!signals) return [];
  const out: ActivityBadge[] = [];

  for (const category of ACTIVITY_CATEGORY_ORDER) {
    const value = signalForCategory(category, signals);
    if (!value || value <= 0) continue;

    const tiers = ACTIVITY_TIERS[category];
    // tiers are highest-first
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      if (value >= tier.threshold) {
        // The next tier up (if any) is the one before this in the array.
        const nextTier = i > 0 ? tiers[i - 1] : undefined;
        out.push({
          id: tier.id,
          name: tier.name,
          emoji: tier.emoji,
          description: tier.description,
          color: tier.color,
          bgColor: tier.bgColor,
          category,
          tier: tiers.length - i, // 1 = lowest, higher = rarer
          nextThreshold: nextTier?.threshold,
        });
        break;
      }
    }
  }

  return out;
}

// Badge event kind for operator-issued badges
export const BADGE_EVENT_KIND = 30009;
export const BADGE_AWARD_KIND = 8;

